/**
 * HeadWithdraw API (본사 입출금현황)
 */
import { apiClient } from "@/shared/api/client";
import {
    GetHeadWithdrawHistoryListResponseDto,
    HeadGetWithdrawableAmountResponseDto,
    HeadRequestWithdrawRequestDto,
    HeadRequestWithdrawResponseDto,
    HeadRequestDepositRequestDto,
    HeadRequestDepositResponseDto,
    HeadWithdrawRequestDetailDto
} from "../types/headWithdraw.types";
import { formatDateToISO, removeDateHyphens } from "@/shared/utils/dateUtils";
import { getItem } from "@/shared/utils/storage";

const BASE_PATH = "/api/v1/headWithdrl";

export const headWithdrawApi = {
    /**
     * 본사 입출금내역 목록 조회
     */
    getHeadWithdrawHistoryList: async (
        fromDate: string,
        toDate: string,
        wdType?: string,
        reqType?: string,
        supplierId?: string,
        supplierName?: string,
        riderId?: string,
        riderName?: string,
        suppType?: string,
    ): Promise<GetHeadWithdrawHistoryListResponseDto> => {
        const formattedFromDate = removeDateHyphens(formatDateToISO(fromDate));
        const formattedToDate = removeDateHyphens(formatDateToISO(toDate));

        let url = `${BASE_PATH}/list?fromDate=${formattedFromDate}&toDate=${formattedToDate}`;

        if (wdType) {
            url += `&wdType=${wdType}`;
        }

        if (reqType) {
            url += `&reqType=${reqType}`;
        }

        if (supplierId) {
            url += `&suppId=${supplierId}`;
        }

        if (supplierName) {
            url += `&supplierName=${encodeURIComponent(supplierName)}`;
        }

        if (riderId) {
            url += `&riderId=${riderId}`;
        }

        if (riderName) {
            url += `&riderName=${encodeURIComponent(riderName)}`;
        }

        if (suppType) {
            url += `&suppType=${suppType}`;
        }

        const response = await apiClient.get<GetHeadWithdrawHistoryListResponseDto>(url);
        return response.data;
    },

    /**
     * 출금 가능 금액 조회
     */
    getWithdrawableAmount: async (): Promise<HeadGetWithdrawableAmountResponseDto> => {
        const url = `${BASE_PATH}/head-withdrawable-amount`;
        const response = await apiClient.get<HeadGetWithdrawableAmountResponseDto>(url);
        return response.data;
    },

    /**
     * 출금 신청
     */
    requestWithdraw: async (
        request: HeadRequestWithdrawRequestDto
    ): Promise<HeadRequestWithdrawResponseDto> => {
        // localStorage에서 userId 가져오기
        const userId = getItem<string>('USER_ID');
        const requestWithInsertUserId = {
            ...request,
            insertUserId: userId || ''
        };
        const response = await apiClient.post<HeadRequestWithdrawResponseDto>(
            `${BASE_PATH}/request`,
            requestWithInsertUserId
        );
        return response.data;
    },

    /**
     * 입금 신청
     */
    requestDeposit: async (
        request: HeadRequestDepositRequestDto
    ): Promise<HeadRequestDepositResponseDto> => {
        const response = await apiClient.post<HeadRequestDepositResponseDto>(
            `${BASE_PATH}/deposit`,
            request
        );
        return response.data;
    },

    /**
     * 출금 신청 내역 리스트
     */
    getWithdrawRequestList: async (
        supplierId?: string,
        status?: 'pending' | 'approved' | 'rejected' | 'completed'
    ): Promise<HeadWithdrawRequestDetailDto[]> => {
        let url = `${BASE_PATH}/requests`;
        const params: string[] = [];

        if (supplierId) {
            params.push(`supplierId=${supplierId}`);
        }

        if (status) {
            params.push(`status=${status}`);
        }

        if (params.length > 0) {
            url += `?${params.join("&")}`;
        }

        const response = await apiClient.get<HeadWithdrawRequestDetailDto[]>(url);
        return response.data;
    },

    /**
     * 협력사 옵션 목록 조회
     * GET /api/v1/headWithdrl/supplier-options?fromDate={fromDate}&toDate={toDate}
     * 응답: string[] (협력사명 배열)
     */
    getSupplierOptions: async (fromDate: string, toDate: string): Promise<Array<{ id: string; name: string }>> => {
        const formattedFromDate = removeDateHyphens(formatDateToISO(fromDate));
        const formattedToDate = removeDateHyphens(formatDateToISO(toDate));
        const response = await apiClient.get<string[]>(
            `${BASE_PATH}/supplier-options`,
            {
                params: {
                    fromDate: formattedFromDate,
                    toDate: formattedToDate
                }
            }
        );
        // 문자열 배열을 { id, name } 형식으로 변환
        return (response.data || []).map((name) => ({
            id: name,
            name: name
        }));
    },

    /**
     * 라이더 옵션 목록 조회
     * GET /api/v1/headWithdrl/rider-options?fromDate={fromDate}&toDate={toDate}&supplierName={supplierName}
     * 응답: string[] (라이더명 배열)
     */
    getRiderOptions: async (fromDate: string, toDate: string, supplierName: string): Promise<Array<{ id: string; name: string }>> => {
        const formattedFromDate = removeDateHyphens(formatDateToISO(fromDate));
        const formattedToDate = removeDateHyphens(formatDateToISO(toDate));
        const response = await apiClient.get<string[]>(
            `${BASE_PATH}/rider-options`,
            {
                params: {
                    fromDate: formattedFromDate,
                    toDate: formattedToDate,
                    supplierName
                }
            }
        );
        // 문자열 배열을 { id, name } 형식으로 변환
        return (response.data || []).map((name) => ({
            id: name,
            name: name
        }));
    },

    /**
     * 입출금 내역 엑셀 다운로드
     * GET /api/v1/headWithdrl/list/excel?fromDate={date}&toDate={date}&wdType={type}&reqType={type}&suppId={id}&supplierName={name}&riderId={id}&riderName={name}
     */
    downloadWithdrawHistoryExcel: async (
        fromDate: string,
        toDate: string,
        wdType?: string,
        reqType?: string,
        supplierId?: string,
        supplierName?: string,
        riderId?: string,
        riderName?: string
    ): Promise<Blob> => {
        const formattedFromDate = removeDateHyphens(formatDateToISO(fromDate));
        const formattedToDate = removeDateHyphens(formatDateToISO(toDate));

        let url = `${BASE_PATH}/list/excel?fromDate=${formattedFromDate}&toDate=${formattedToDate}`;

        if (wdType) {
            url += `&wdType=${wdType}`;
        }

        if (reqType) {
            url += `&reqType=${reqType}`;
        }

        if (supplierId) {
            url += `&suppId=${supplierId}`;
        }

        if (supplierName) {
            url += `&supplierName=${encodeURIComponent(supplierName)}`;
        }

        if (riderId) {
            url += `&riderId=${riderId}`;
        }

        if (riderName) {
            url += `&riderName=${encodeURIComponent(riderName)}`;
        }

        const response = await apiClient.get<Blob>(url, {
            responseType: 'blob'
        });
        return response.data;
    }
};
